import { format } from "date-fns";
import { UseFormReturn } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { groupBy } from "../../../lib/utils";
import { PlusCircleIcon } from "../../../ui/icons/plusCircle";
import Button from "../../../ui/molecules/button";
import Input from "../../../ui/molecules/input";
import { SelectCustom } from "../../../ui/molecules/select";
import TextArea from "../../../ui/molecules/textArea";
import { UserRole } from "../../users/dto";
import { SelectUser } from "../../users/select/userSelect";
import { setCreateFromProject } from "../../users/slice";
import { ProjectStateEnum } from "../dto";
import { ProjectCreationFormData } from "./validation";

interface Props {
    form: UseFormReturn<ProjectCreationFormData>;
}
export function Form({ form }: Props) {
    const atecosState = useAppSelector(state => state.atecos)
    const usersState = useAppSelector(state => state.users)

    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const customer = usersState.findClientsResponse?.data.find(client => client.id === form.watch('customerId'))

    return (
        <div className="gap-[24px] h-full max-h-full flex flex-col w-[100%] p-[16px]">
            <div className="p-[24px] gap-[24px] h-full max-h-full overflow-auto flex flex-col bg-white shadow-sm rounded-[12px]">
                <div className="flex gap-6 items-flex-start">
                    <Input
                        key={'new-project-name'}
                        label="Nome del progetto"
                        defaultValue={form.watch('name')}
                        value={form.watch('name')}
                        onChangeText={e => form.setValue('name', e)}
                        error={!!form.formState.errors.name}
                        supportingText={form.formState.errors.name?.message as string | undefined}
                    />
                    <div className="flex w-full items-end">
                        <SelectUser
                            isMulti={true}
                            id="select-commercials-project-filters"
                            role={UserRole.commercial}
                            value={form.watch('commercialIds')}
                            onChange={(v) => form.setValue('commercialIds', v ?? [])}
                            label="Commerciali"
                            placeholder={"Seleziona i commerciali"}
                            isClearable
                            error={!!form.formState.errors.commercialIds}
                            errorLabel={form.formState.errors.commercialIds?.message as string | undefined}
                        />
                        <Button
                            onClick={() => {
                                dispatch(setCreateFromProject({ userRole: UserRole.commercial }))
                                navigate('/new-user')
                            }}
                            size={"md"}
                            iconPosition={"only"}
                            variant={"ghost"}
                            color={"blue"}
                            icon={<PlusCircleIcon color={""} size={0} />}
                        />
                    </div>
                    <div className="flex w-full items-end">
                        <SelectUser
                            isMulti={false}
                            id="select-teamleaders-project-filters"
                            role={UserRole.team_leader}
                            value={form.watch('teamLeaderId')}
                            onChange={(v) => form.setValue('teamLeaderId', v!)}
                            label="Team Leader"
                            placeholder={"Seleziona il team leader"}
                            isClearable
                            error={!!form.formState.errors.teamLeaderId}
                            errorLabel={form.formState.errors.teamLeaderId?.message as string | undefined}
                        />
                        <Button
                            onClick={() => {
                                dispatch(setCreateFromProject({ userRole: UserRole.team_leader }))
                                navigate('/new-user')
                            }}
                            size={"md"}
                            iconPosition={"only"}
                            variant={"ghost"}
                            color={"blue"}
                            icon={<PlusCircleIcon color={""} size={0} />}
                        />
                    </div>
                </div>
                <div className="h-[1px] w-full bg-neutral-200" />
                <div className="flex gap-6 items-start">
                    <div className="flex w-full items-end">
                        <SelectUser
                            isMulti={false}
                            id="select-customer-project-filters"
                            role={UserRole.customer}
                            value={form.watch('customerId')}
                            onChange={(v) => form.setValue('customerId', v!)}
                            label="Ragione Sociale"
                            placeholder={"Seleziona il cliente"}
                            isClearable
                            error={!!form.formState.errors.customerId}
                            errorLabel={form.formState.errors.customerId?.message as string | undefined}
                        />
                        <Button
                            onClick={() => {
                                dispatch(setCreateFromProject({ userRole: UserRole.customer }))
                                navigate('/new-user')
                            }}
                            size={"md"}
                            iconPosition={"only"}
                            variant={"ghost"}
                            color={"blue"}
                            icon={<PlusCircleIcon color={""} size={0} />}
                        />
                    </div>
                    <Input label="P. IVA" disabled value={customer ? customer.companyVat : ''} />
                    <Input label="Referente" disabled value={customer ? (customer.name + ' ' + customer.surname) : ''} />
                </div>
                <div className="flex gap-6 items-start">
                    <SelectCustom placeholder={""} disabled defaultValue={customer?.companyAtecoCode} options={atecosState.findAllAtecosResponse.map(ateco => ({ value: ateco.atecoCode, label: ateco.atecoCode + ' - ' + ateco.atecoDescription }))} label="Cod. ATECO" />
                    <SelectCustom placeholder={""} disabled defaultValue={customer?.companySector} options={groupBy(atecosState.findAllAtecosResponse, 'sector').map(sector => ({ label: sector.key, value: sector.key }))} label="Settore" />
                    <NumericFormat
                        prefix="€ "
                        value={customer?.companyRevenue ?? 0}
                        thousandSeparator="."
                        decimalSeparator=","
                        decimalScale={2}
                        fixedDecimalScale
                        customInput={Input}
                        id={"new-user-request-company-revenue"}
                        key={"new-user-request-company-revenue"}
                        placeholder={"Fatturato"}
                        label="Fatturato"
                        disabled
                    />
                </div>
                <div className="flex gap-6 items-start">
                    <SelectCustom placeholder={""} defaultValue={ProjectStateEnum.TOSTART} options={[{ label: "Da iniziare", value: ProjectStateEnum.TOSTART }]} disabled label="Stato" />
                    <Input disabled label="Data inizio" value={format(new Date(), 'dd/MM/yyyy')} />
                    <Input disabled label="Data fine" placeholder="Data fine" />
                </div>
                <div className="flex gap-6 items-start">
                    <Input onChangeText={(e) => form.setValue('emailFrom', e)} label="Email di progetto" />
                    <Input onChangeText={(e) => form.setValue('nameFrom', e)} label="Nome mittente email di progetto" />
                </div>
                <TextArea label="Descrizione" defaultValue={form.watch('description')!} onChangeText={e => form.setValue('description', e)} />
            </div>
        </div>
    )
}